import React, {useState} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {FlexColumn, FlexRow, Label, Pill, PillVariant, Text} from '@forecast-it/design-system';
import {PromptTarget} from '../state/useNovaPromptState';
import {
	NovaPromptInput,
	NovaVariable,
	NovaVariableScope,
	SCOPE_ORDER,
	groupVariablesByScope,
} from '../shared/novaVariableLibrary';
import {NovaStudioColor} from '../shared/novaStudioColors';
import {NOVA_MONOSPACE_FONT} from '../shared/novaStudioFonts';

const GROUP_LABEL_ID: Record<NovaVariableScope, string> = {
	TASK: 'nova.variableLibrary.groupTask',
	PROJECT_CONTEXT: 'nova.variableLibrary.groupProjectContext',
	FILES: 'nova.variableLibrary.groupFiles',
};

const SOURCE_MESSAGE_ID: Record<NovaVariableScope, string> = {
	TASK: 'nova.variableLibrary.sourceTask',
	PROJECT_CONTEXT: 'nova.variableLibrary.sourceProjectContext',
	FILES: 'nova.variableLibrary.sourceFiles',
};

// Human, localised names for each prompt input, used to explain where a denied variable *is*
// allowed. The raw enum values are never shown to the author.
const INPUT_LABEL_ID: Record<NovaPromptInput, string> = {
	SYSTEM_PROMPT: 'nova.variableLibrary.inputSystemPrompt',
	USER_PROMPT_TEMPLATE: 'nova.variableLibrary.inputUserPrompt',
	AGENT_MD: 'nova.variableLibrary.inputAgentMd',
};

// Files is the one scope that can legitimately be empty (no attachments in v1); it still renders a
// labelled empty row so the group reads as "nothing yet", not a broken list.
const SCOPES_WITH_EMPTY_STATE: NovaVariableScope[] = ['FILES'];

interface NovaVariableLibraryProps {
	variables: NovaVariable[];
	activeTarget: PromptTarget;
	isPermitted: (token: string, target: PromptTarget) => boolean;
	onInsert: (token: string) => void;
}

const Container = styled.div`
	border: 1px solid ${NovaStudioColor.border};
	border-radius: 6px;
`;

// The collapsible header. Kept as a real <button> for the aria-expanded / keyboard semantics the DS
// layout primitives don't provide; its inner groupings use FlexRow.
const Bar = styled.button`
	all: unset;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	width: 100%;
	box-sizing: border-box;
	padding: 10px 16px;
	cursor: pointer;

	&:focus-visible {
		outline: 2px solid ${NovaStudioColor.focusRing};
		outline-offset: -2px;
	}
`;

// The expanded region. Only the border and padding are bespoke chrome; the vertical stacking is done
// with FlexColumn inside.
const Body = styled.div`
	border-top: 1px solid ${NovaStudioColor.border};
	padding: 12px 16px;
`;

// A clickable variable row laid out as three aligned columns (token · description/source · status).
// A fixed first column makes the tokens line up down the list, and the middle column takes the
// remaining width. The button/disabled/hover semantics can't come from a DS layout primitive, so the
// row itself stays a styled <button>; its cell contents are DS components. It must be a direct,
// full-width child of its column — do not wrap it in a shrink-to-fit container, or the grid columns
// stop aligning down the list.
const Row = styled.button<{$disabled: boolean}>`
	all: unset;
	display: grid;
	grid-template-columns: 200px minmax(0, 1fr) auto;
	align-items: start;
	column-gap: 16px;
	padding: 6px 8px;
	border-radius: 4px;
	box-sizing: border-box;
	cursor: ${({$disabled}) => ($disabled ? 'not-allowed' : 'pointer')};
	opacity: ${({$disabled}) => ($disabled ? 0.6 : 1)};

	&:hover {
		background-color: ${({$disabled}) => ($disabled ? 'transparent' : NovaStudioColor.badgeBackground)};
	}

	&:focus-visible {
		outline: 2px solid ${NovaStudioColor.focusRing};
		outline-offset: -2px;
	}
`;

const Token = styled.span`
	font-family: ${NOVA_MONOSPACE_FONT};
`;

const EmptyRow = styled.div`
	padding: 6px 8px;
`;

function tokenSlug(token: string): string {
	return token.replace(/[{}]/g, '');
}

export function NovaVariableLibrary({variables, activeTarget, isPermitted, onInsert}: NovaVariableLibraryProps): JSX.Element {
	const {formatMessage} = useIntl();
	const [expanded, setExpanded] = useState(false);
	const groups = groupVariablesByScope(variables);
	const scopesWithVariables = new Set(groups.map(group => group.scope));
	const missingEmptyStateScopes = SCOPES_WITH_EMPTY_STATE.filter(scope => !scopesWithVariables.has(scope));
	const allGroups = [...groups, ...missingEmptyStateScopes.map(scope => ({scope, variables: [] as NovaVariable[]}))].sort(
		(first, second) => SCOPE_ORDER.indexOf(first.scope) - SCOPE_ORDER.indexOf(second.scope)
	);

	const targetLabel = formatMessage({
		id: activeTarget === PromptTarget.System ? 'nova.variableLibrary.targetSystem' : 'nova.variableLibrary.targetUser',
	});
	const countLabel = formatMessage({id: 'nova.variableLibrary.count'}, {count: variables.length});
	const deprecatedLabel = formatMessage({id: 'nova.variableLibrary.deprecated'});

	// The reason a variable is greyed out in the active target, worded from its own permitted inputs
	// rather than a single fixed string — a variable allowed only in the System prompt reads
	// differently from a customer-data variable allowed only in the User prompt.
	const describeDenyReason = (variable: NovaVariable): string => {
		if (variable.permittedInputs.length === 0) {
			return formatMessage({id: 'nova.variableLibrary.denyUnavailable'});
		}
		const inputs = variable.permittedInputs.map(input => formatMessage({id: INPUT_LABEL_ID[input]})).join(', ');
		return formatMessage({id: 'nova.variableLibrary.denyAvailableIn'}, {inputs});
	};

	return (
		<Container data-cy="nova-variable-library" data-testid="nova-variable-library">
			<Bar
				type="button"
				data-cy="nova-variable-library-bar"
				data-testid="nova-variable-library-bar"
				aria-expanded={expanded}
				onClick={() => setExpanded(previous => !previous)}
			>
				<FlexRow alignItems="baseline" gap="s">
					<Text>{formatMessage({id: 'nova.variableLibrary.title'})}</Text>
					<Text color="auxiliary">{formatMessage({id: 'nova.variableLibrary.hint'})}</Text>
				</FlexRow>
				<FlexRow alignItems="baseline" gap="s">
					<Pill variant={PillVariant.NEUTRAL}>{targetLabel}</Pill>
					<Text color="auxiliary">{countLabel}</Text>
				</FlexRow>
			</Bar>
			{expanded && (
				<Body data-cy="nova-variable-library-body" data-testid="nova-variable-library-body">
					<FlexColumn gap="l">
						{allGroups.map(group => (
							// Plain wrapper carries the group test selector: DS FlexColumn does not forward
							// data-cy, so the group scope tag lives on this div and FlexColumn does the layout.
							<div
								key={group.scope}
								data-cy={`nova-variable-library-group-${group.scope}`}
								data-testid={`nova-variable-library-group-${group.scope}`}
							>
								<FlexColumn gap="s">
									<Label>{formatMessage({id: GROUP_LABEL_ID[group.scope]})}</Label>
									{group.variables.length === 0 ? (
										<EmptyRow
											data-cy={`nova-variable-library-${group.scope.toLowerCase()}-empty`}
											data-testid={`nova-variable-library-${group.scope.toLowerCase()}-empty`}
										>
											<Text color="auxiliary">
												{formatMessage({id: 'nova.variableLibrary.filesEmpty'})}
											</Text>
										</EmptyRow>
									) : (
										<FlexColumn gap="xs">
											{group.variables.map(variable => {
												const permitted = isPermitted(variable.token, activeTarget);
												const slug = tokenSlug(variable.token);
												const isDeprecated = variable.lifecycleState === 'DEPRECATED';
												const sourceLabel = formatMessage(
													{id: 'nova.variableLibrary.sourcePrefix'},
													{source: formatMessage({id: SOURCE_MESSAGE_ID[variable.scope]})}
												);

												return (
													<Row
														key={variable.token}
														type="button"
														$disabled={!permitted}
														aria-disabled={!permitted}
														data-cy={`nova-variable-${slug}`}
														data-testid={`nova-variable-${slug}`}
														onClick={() => {
															if (!permitted) {
																return;
															}
															onInsert(variable.token);
														}}
													>
														<Token>{variable.token}</Token>
														<FlexColumn gap="xxs" alignItems="flex-start">
															<Text>{variable.description}</Text>
															<Text color="auxiliary">{sourceLabel}</Text>
														</FlexColumn>
														<FlexColumn gap="xs" alignItems="flex-end">
															{isDeprecated && (
																<Pill variant={PillVariant.NEUTRAL}>{deprecatedLabel}</Pill>
															)}
															{!permitted && (
																<Text color="auxiliary">{describeDenyReason(variable)}</Text>
															)}
														</FlexColumn>
													</Row>
												);
											})}
										</FlexColumn>
									)}
								</FlexColumn>
							</div>
						))}
					</FlexColumn>
				</Body>
			)}
		</Container>
	);
}

export default NovaVariableLibrary;
